import React, { useContext, useState, useEffect, useRef } from 'react';
import { STWContext } from '../../widgets/STW/context';
import InfoIcon from '../../assets/svg/info';
import useIsMobile from '../../hooks/useIsMobile';
import { LanguageUtils } from '../../utilities/languageUtilities';
import { LANGUAGE_VALUES } from '@bikdotai/bik-models/dm';

interface EmailInputProps {
  onChangeHandle: (
    value: string,
    key: 'isWhatsapp' | 'isSms' | 'isEmail',
  ) => void;
  value: string;
  error: string;
  title?: string;
  skipLabel?: boolean;
  id?: string;
  fontColour: string;
  onBlur: (key: 'isWhatsapp' | 'isSms' | 'isEmail') => void;
  onFocusColour?: string;
  size?: 'small' | 'large';
  widgetLanguage: LANGUAGE_VALUES;
}

const EmailInput: React.FC<EmailInputProps> = ({
  onChangeHandle,
  onFocusColour,
  value,
  title,
  error,
  skipLabel,
  id,
  fontColour,
  onBlur,
  size,
  widgetLanguage,
}) => {
  const { preview, isMobile } = useContext(STWContext);
  const isMobileDevice = useIsMobile();
  const languageUtilities = new LanguageUtils();
  const [currentStyle, setCurrentStyle] = useState<any>(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const importStyles = async () => {
      let stylesModule;
      if (preview) {
        if (isMobile) {
          stylesModule = await import(
            '../WhatsappInput/whatsappInputPreviewMobile.module.css'
          );
        } else {
          stylesModule = await import(
            '../WhatsappInput/whatsappInputPreviewDesktop.module.css'
          );
        }
      } else {
        stylesModule = await import(
          '../WhatsappInput/whatsappInput.module.css'
        );
      }
      setCurrentStyle(stylesModule.default);
    };
    importStyles().then();
  }, [preview, isMobile]);

  useEffect(() => {
    if (inputRef.current) {
      if (error && onFocusColour) {
        inputRef.current.style.border = `1px solid #b92321`;
      } else {
        inputRef.current.style.border = `1px solid ${onFocusColour}`;
      }
    }
  }, [error]);

  if (!currentStyle) {
    return <></>;
  }

  return (
    <div className={currentStyle.bikEmailInputMain}>
      {!skipLabel && (
        <div className={currentStyle.bikStwH2} style={{ color: fontColour }}>
          {title ?? languageUtilities.getEmailPlaceholder(widgetLanguage, true)}
        </div>
      )}
      <div className={currentStyle.bikInputWrapper}>
        <input
          ref={inputRef}
          id={id}
          onBlur={() => {
            if (onFocusColour) {
              inputRef.current.style.border = `1px solid #e0e0e0`;
            }
            onBlur('isEmail');
          }}
          onFocus={() => {
            if (onFocusColour) {
              inputRef.current.style.border = `1px solid ${onFocusColour}`;
            }
          }}
          type="email"
          style={
            size === 'small' || preview
              ? { color: fontColour }
              : { height: isMobileDevice ? 38 : 48, color: fontColour }
          }
          onChange={e => onChangeHandle(e.target.value, 'isEmail')}
          value={value}
          placeholder={languageUtilities.getEmailPlaceholder(
            widgetLanguage,
            skipLabel,
          )}
        />
        {error && (
          <div className={currentStyle.bikStwError}>
            <InfoIcon />
            <div>{' ' + error}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailInput;
